import { API } from './rest';
import Stripe from 'stripe';

/**
 * A user-util Cognito user
 * (duped in user-util.js)
 */
type CognitoUser = {
  username: string;
  userCreateDate: string;
  userLastModifiedDate: string;
  userStatus: string;
  enabled: boolean;
  attrs: {
    sub: string;
    email: string;
    email_verified?: boolean;
    name?: string;
    given_name?: string;
    'custom:identity_id'?: string;
    identities?: {
      dateCreated: number;
      issuer: any;
      primary: boolean;
      providerName: string;
      providerType: string;
      userId: string;
    }[];
  };
};

/**
 * Fields set on fpusers and fporgs from a Stripe subscription */
type SubscriptionDbUpdates = {
  subscriptionId: string;
  customerId: string;
  planId: string;
  checkoutType?: 'stripe' | 'classic' | null;
  current_period_start: number;
  current_period_end: number;
  cancel_at_period_end: boolean;
  trial_end: number;
  status:
    | 'incomplete'
    | 'incomplete_expired'
    | 'trialing'
    | 'active'
    | 'past_due'
    | 'canceled'
    | 'unpaid';
  tax_percent: number | null;
  amount: number;
  interval: 'month' | 'year' | 'week' | 'day';
  interval_count: number;
  postal_code?: string;
};

type AbTestV2 = {
  bucket: number;
  created: number;
  group: 'classic' | 'stripe';
  trial_start?: number;
  sub_start?: number;
  no_track?: boolean;
};

/**
 * Fields shared between fpusers and fporgs
 */
type DbSharedFields = {
  created: number;
  updated?: number;
  consent_promotions?: 'opt_in';
  prevSubLastCreated?: number;
  prevSubCount?: number;
} & Partial<SubscriptionDbUpdates>;

type OrgMemberRole = 'admin' | 'user';

type OrgMemberStatus = 'invited' | 'active' | 'deactivated' | 'archived';

type AbTestAbandoned = {
  sessionId: string;
  consent: string | null;
  created: number;
  completed: number;
  count: number;
};

/** An fpusers user record */
type DbUser = {
  userId: string;
  type: 'main' | 'temp';
  sub: string; // Cognito "sub" value
  email?: string;
  orgId: string;
  orgRole?: OrgMemberRole;
  orgStatus?: OrgMemberStatus;
  orgName?: string;
  orgAdminEmail?: string;
  accept_language?: string;
  abTestV2?: AbTestV2;
  abTestAban?: AbTestAbandoned;
  testClock?: string;
} & DbSharedFields;

/**
 * An fporg organization record */
type DbOrg = {
  orgId: string;
  type: 'main';
  name: string;
  seats: number;
  adminEmail: string;
} & DbSharedFields;

/** The user info returned from an admin query */
export type AdminQueryUser = {
  cognito?: CognitoUser;
  dbUser?: DbUser;
  dbOrg?: DbOrg;
  stripeCustomer?: Stripe.Customer;
};

export const queryUser = (search: string) => {
  return API.get({ path: `/admin/users?q=${encodeURIComponent(search)}` }).then(
    (resp) => {
      return resp as AdminQueryUser;
    },
  );
};

export const adminQueryUserIsBlank = (user?: AdminQueryUser) => {
  return !user || (!user.cognito && !user.dbUser && !user.stripeCustomer);
};

//

export const setUserVerified = (cognitoSub: string) => {
  return API.post({ path: `/admin/users/verify`, body: { cognitoSub } }).then(
    (resp) => {
      return resp as unknown;
    },
  );
};

//

export const deleteUser = (cognitoSub: string) => {
  return API.del({ path: `/admin/users/delete`, body: { cognitoSub } }).then(
    (resp) => {
      return resp as { type: 'success' | 'error'; message: string };
    },
  );
};

//

/**
 * Cancel the Stripe subscription for the given
 * user.
 */
export const cancelSubscription = ({
  userId,
  subscriptionId,
}: {
  userId: string;
  subscriptionId: string;
}) => {
  return API.post({
    path: `/admin/users/cancel-subscription`,
    body: { userId, subscriptionId },
  }).then((resp) => {
    return resp as { type: 'success' | 'error'; message: string };
  });
};

//

/** All user Stripe statuses that represent a subscription that could be canceled */
export const ACTIVELIKE_USER_STATUSES: SubscriptionDbUpdates['status'][] = [
  'active',
  'past_due',
  'trialing',
];

/** All org orgStatus values that count as being a member of an org */
export const ACTIVELIKE_ORG_STATUSES: OrgMemberStatus[] = ['active', 'invited'];
